import { useState } from 'react'

import { IconButton, ThemeProvider } from '@mui/material'
import { DateCalendar } from '@mui/x-date-pickers/DateCalendar'
import { ArrowLeftIcon, ArrowRightIcon } from '@mui/x-date-pickers/icons'
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader'
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay'
import classNames from 'classnames'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'

import { calendarTheme } from './theme'
import type { DateRangeValue } from './useDateRangePopover'
import './DateRangeCalendar.scss'

export interface DateRangeCalendarProps {
  value: DateRangeValue
  onChange: (value: DateRangeValue) => void
  calendars?: 1 | 2
  maxDays?: number
}

interface RangeDayProps extends PickersDayProps {
  rangeFrom?: Dayjs | null
  rangeTo?: Dayjs | null
  hoverDay?: Dayjs | null
  onHoverDay?: (day: Dayjs | null) => void
  maxDays?: number
}

const RangeDay = ({
  day,
  rangeFrom,
  rangeTo,
  hoverDay,
  onHoverDay,
  maxDays,
  ...pickersDayProps
}: RangeDayProps) => {
  const outside = pickersDayProps.outsideCurrentMonth
  const hasRange = !!(rangeFrom && rangeTo)

  const isOutOfRange =
    !outside &&
    !!(
      rangeFrom &&
      !rangeTo &&
      maxDays &&
      Math.abs(day.startOf('day').diff(rangeFrom.startOf('day'), 'day')) >=
        maxDays
    )

  const isStart =
    hasRange && !outside && (rangeFrom?.isSame(day, 'day') ?? false)
  const isEnd = hasRange && !outside && (rangeTo?.isSame(day, 'day') ?? false)
  const isInRange =
    hasRange &&
    !outside &&
    day.isAfter(rangeFrom!, 'day') &&
    day.isBefore(rangeTo!, 'day')
  const isSelected =
    !outside &&
    ((rangeFrom?.isSame(day, 'day') ?? false) ||
      (rangeTo?.isSame(day, 'day') ?? false))

  const previewActive = !!(
    rangeFrom &&
    !rangeTo &&
    hoverDay &&
    !hoverDay.isSame(rangeFrom, 'day')
  )
  const previewMin = previewActive
    ? hoverDay!.isBefore(rangeFrom!, 'day')
      ? hoverDay!
      : rangeFrom!
    : null
  const previewMax = previewActive
    ? hoverDay!.isAfter(rangeFrom!, 'day')
      ? hoverDay!
      : rangeFrom!
    : null

  const isHoverRange =
    !outside &&
    !!(
      previewMin &&
      previewMax &&
      day.isAfter(previewMin, 'day') &&
      day.isBefore(previewMax, 'day')
    )
  const isHoverStart =
    !outside && previewActive && (previewMin?.isSame(day, 'day') ?? false)
  const isHoverEnd =
    !outside && previewActive && (previewMax?.isSame(day, 'day') ?? false)

  const isFirstOfWeek = day.isSame(day.startOf('week'), 'day')
  const isLastOfWeek = day.isSame(day.endOf('week'), 'day')
  const isFirstOfMonth = day.isSame(day.startOf('month'), 'day')
  const isLastOfMonth = day.isSame(day.endOf('month'), 'day')
  const isRowStart =
    (isInRange || isHoverRange || isEnd || isHoverEnd) &&
    (isFirstOfWeek || isFirstOfMonth)
  const isRowEnd =
    (isInRange || isHoverRange || isStart || isHoverStart) &&
    (isLastOfWeek || isLastOfMonth)

  return (
    <div
      className={classNames('date-range-picker__day', {
        'date-range-picker__day--start': isStart,
        'date-range-picker__day--end': isEnd,
        'date-range-picker__day--in-range': isInRange,
        'date-range-picker__day--hover-range': isHoverRange,
        'date-range-picker__day--hover-start': isHoverStart,
        'date-range-picker__day--hover-end': isHoverEnd,
        'date-range-picker__day--row-start': isRowStart,
        'date-range-picker__day--row-end': isRowEnd,
      })}
      onMouseEnter={() => !isOutOfRange && onHoverDay?.(day)}
      onMouseLeave={() => onHoverDay?.(null)}
    >
      <PickersDay
        day={day}
        {...pickersDayProps}
        selected={isSelected}
        disabled={pickersDayProps.disabled || isOutOfRange}
      />
    </div>
  )
}

interface CalendarHeaderProps extends PickersCalendarHeaderProps {
  onPrev?: () => void
  onNext?: () => void
  disablePrev?: boolean
  disableNext?: boolean
}

const CalendarHeader = ({
  currentMonth,
  onPrev,
  onNext,
  disablePrev,
  disableNext,
}: CalendarHeaderProps) => {
  const { t, i18n } = useTranslation()
  const label = new Intl.DateTimeFormat(i18n.language, {
    month: 'long',
    year: 'numeric',
  }).format(currentMonth.toDate())

  return (
    <div className="date-range-picker__header">
      <IconButton
        size="medium"
        onClick={onPrev}
        disabled={disablePrev}
        aria-label={t('components.dateRangePicker.previousMonth')}
      >
        <ArrowLeftIcon />
      </IconButton>
      <span className="date-range-picker__month-label">{label}</span>
      <IconButton
        size="medium"
        onClick={onNext}
        disabled={disableNext}
        aria-label={t('components.dateRangePicker.nextMonth')}
      >
        <ArrowRightIcon />
      </IconButton>
    </div>
  )
}

interface SingleCalendarProps {
  month: Dayjs
  onMonthChange: (m: Dayjs) => void
  minMonth?: Dayjs
  maxMonth?: Dayjs
  rangeFrom: Dayjs | null
  rangeTo: Dayjs | null
  hoverDay: Dayjs | null
  maxDays?: number
  onDayClick: (day: Dayjs | null) => void
  onHoverDay: (day: Dayjs | null) => void
}

const SingleCalendar = ({
  month,
  onMonthChange,
  minMonth,
  maxMonth,
  rangeFrom,
  rangeTo,
  hoverDay,
  maxDays,
  onDayClick,
  onHoverDay,
}: SingleCalendarProps) => {
  const headerSlotProps = {
    onPrev: () => onMonthChange(month.subtract(1, 'month')),
    onNext: () => onMonthChange(month.add(1, 'month')),
    disablePrev: !!(minMonth && !month.isAfter(minMonth, 'month')),
    disableNext: !!(maxMonth && !month.isBefore(maxMonth, 'month')),
  } as unknown as PickersCalendarHeaderProps

  const daySlotProps = {
    rangeFrom,
    rangeTo,
    hoverDay,
    onHoverDay,
    maxDays,
  } as unknown as PickersDayProps

  return (
    <DateCalendar
      key={month.format('YYYY-MM')}
      value={null}
      onChange={onDayClick}
      referenceDate={month}
      views={['day']}
      disableHighlightToday
      slots={{
        calendarHeader: CalendarHeader,
        day: RangeDay as typeof PickersDay,
      }}
      slotProps={{ calendarHeader: headerSlotProps, day: daySlotProps }}
    />
  )
}

export const DateRangeCalendar = ({
  value,
  onChange,
  calendars = 2,
  maxDays,
}: DateRangeCalendarProps) => {
  const [from, to] = value
  const [hoverDay, setHoverDay] = useState<Dayjs | null>(null)
  const [leftMonth, setLeftMonth] = useState<Dayjs>(from ?? dayjs())
  const [rightMonth, setRightMonth] = useState<Dayjs>(
    from?.add(1, 'month') ?? dayjs().add(1, 'month'),
  )

  const clampedHoverDay = (() => {
    if (!hoverDay || !from || to || !maxDays) return hoverDay
    const diff = hoverDay.startOf('day').diff(from.startOf('day'), 'day')
    if (Math.abs(diff) < maxDays) return hoverDay
    return diff > 0
      ? from.add(maxDays - 1, 'day')
      : from.subtract(maxDays - 1, 'day')
  })()

  const handleDayClick = (day: Dayjs | null) => {
    if (!day) return
    if (!from || to) {
      onChange([day, null])
    } else {
      if (
        maxDays &&
        Math.abs(day.startOf('day').diff(from.startOf('day'), 'day')) >= maxDays
      ) {
        return
      }
      setHoverDay(null)
      onChange(day.isBefore(from, 'day') ? [day, from] : [from, day])
    }
  }

  const sharedProps = {
    rangeFrom: from,
    rangeTo: to,
    hoverDay: clampedHoverDay,
    maxDays,
    onDayClick: handleDayClick,
    onHoverDay: setHoverDay,
  }

  return (
    <ThemeProvider theme={calendarTheme}>
      <div className="date-range-picker">
        <SingleCalendar
          month={leftMonth}
          onMonthChange={setLeftMonth}
          maxMonth={
            calendars === 2 ? rightMonth.subtract(1, 'month') : undefined
          }
          {...sharedProps}
        />
        {calendars === 2 && (
          <SingleCalendar
            month={rightMonth}
            onMonthChange={setRightMonth}
            minMonth={leftMonth.add(1, 'month')}
            {...sharedProps}
          />
        )}
      </div>
    </ThemeProvider>
  )
}

export type {
  DateRangeValue,
  DateRangeStringValue,
} from './useDateRangePopover'
export { useDateRangePopover } from './useDateRangePopover'
